// src/components/customer/imageUploader/ImageUploader.jsx
import React, { useState } from "react";
import "../../../assets/css/customer/imageUploader/ImageUploader.css";

const ImageUploader = ({ images, onChange, maxImages = 5 }) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
    // Reset input so same file can be re-selected
    e.target.value = "";
  };

  // ✅ FIX: "images" prop can contain either raw File objects (first ever
  // selection) OR already-converted objects like {file, preview, base64, ...}
  // (after CreateBidding has processed a previous batch). Before merging with
  // newly selected files, always pull out the real underlying File so the
  // array we send to onChange is a clean, consistent list of File objects —
  // whether the user selects images one at a time or all together.
  const getFileObject = (image) => {
    if (image instanceof File) return image;
    if (image?.file instanceof File) return image.file;
    return image; // fallback, shouldn't normally happen
  };

  const handleFiles = (files) => {
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (images.length + imageFiles.length > maxImages) {
      alert(`You can only upload up to ${maxImages} images`);
      return;
    }

    const existingFiles = images.map(getFileObject);

    // Pass a clean array of raw File objects to parent
    // (CreateBidding handles base64/preview conversion)
    onChange([...existingFiles, ...imageFiles]);
  };

  const handleRemoveImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onChange(newImages.map(getFileObject));
  };

  // Helper: get preview URL — works for both File objects and already-converted objects
  const getPreviewSrc = (image) => {
    if (!image) return null;
    if (typeof image === "string") return image;           // plain URL string
    if (image.preview) return image.preview;               // converted object {preview, base64, ...}
    if (image instanceof File) return URL.createObjectURL(image); // raw File
    return null;
  };

  return (
    <div className="imgup-container">
      {/* Upload Zone */}
      <div
        className={`imgup-upload-zone ${dragActive ? "imgup-drag-active" : ""}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="fileInput"
          className="imgup-file-input"
          multiple
          accept="image/*"
          onChange={handleFileInput}
          disabled={images.length >= maxImages}
        />
        <label htmlFor="fileInput" className="imgup-upload-label">
          <div className="imgup-upload-icon">📤</div>
          <div className="imgup-upload-text">
            <span className="imgup-upload-title">
              Drop images here or click to upload
            </span>
            <span className="imgup-upload-subtitle">
              PNG, JPG up to 5MB ({images.length}/{maxImages})
            </span>
          </div>
        </label>
      </div>

      {/* Image Preview Grid — real images */}
      {images.length > 0 && (
        <div className="imgup-preview-grid">
          {images.map((image, index) => {
            const src = getPreviewSrc(image);
            return (
              <div key={index} className="imgup-preview-item">
                {index === 0 && <div className="imgup-main-badge">Main</div>}
                <div className="imgup-preview-image">
                  {src ? (
                    <img
                      src={src}
                      alt={`Product ${index + 1}`}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <span className="imgup-preview-emoji">📸</span>
                  )}
                </div>
                <button
                  type="button"
                  className="imgup-remove-btn"
                  onClick={() => handleRemoveImage(index)}
                  title="Remove image"
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ImageUploader;